// ISCC certificate Material / Annex I column definitions.
//
// Ported from the legacy AuditQ system, which is the authority for what a certificate
// must contain: auditqv2-main/resources/views/modules/tools/audit/process-audit/
// certificate_production_draft.blade.php (Material table at ~205-700, Annex I at ~812-980)
// and app/Helpers/ISCC.php for the scheme list and the low-LUC rule.
//
// The Material table is SCHEME-SPECIFIC -- it is not one shared column set. The CRM's
// FTRD-02 form previously rendered a single generic 7-column table whose last two columns
// ("Raw Material Criteria", "Add-ons") do not exist in the ISCC EU material table at all.
//
// Legacy field names are preserved verbatim as the `field` keys so saved documents,
// the issuance payload and the PDF renderer all speak the same language as AuditQ.

import { isIsccStandard } from './isccStandards';

export const ISCC_SCHEMES = {
  EU: 'EU',
  PLUS: 'PLUS',
  JAPAN_FIT: 'JAPAN_FIT',
  CORSIA: 'CORSIA',
  CORSIA_PLUS: 'CORSIA_PLUS',
  CFC: 'CFC',
};

const readText = (value) => String(
  typeof value === 'string'
    ? value
    : value?.standardName || value?.standard_name || value?.name || value?.label || value?.value || '',
).trim();

/**
 * Which ISCC scheme a standard label names. Order matters: CORSIA PLUS and CORSIA both
 * contain "CORSIA", and ISCC PLUS also contains "PLUS", so the most specific label is
 * tested first. Returns null for anything that is not an ISCC standard.
 */
export const resolveIsccScheme = (value) => {
  const text = readText(value);
  if (!text || !isIsccStandard(text)) return null;
  const upper = text.toUpperCase();
  if (upper.includes('CORSIA') && upper.includes('PLUS')) return ISCC_SCHEMES.CORSIA_PLUS;
  if (upper.includes('CORSIA')) return ISCC_SCHEMES.CORSIA;
  if (/\bJAPAN[\s_-]*FIT\b/.test(upper) || upper.includes('JAPAN')) return ISCC_SCHEMES.JAPAN_FIT;
  if (upper.includes('CFC')) return ISCC_SCHEMES.CFC;
  if (upper.includes('PLUS')) return ISCC_SCHEMES.PLUS;
  // EU is the legacy `else` branch: the default material layout, also used by PLUS and CFC.
  return ISCC_SCHEMES.EU;
};

/**
 * Low-LUC is a CORSIA / CORSIA PLUS sub-variant that replaces the whole material table
 * with three different columns. Legacy rule (ISCC.php ISCC_is_iscc_corsia_with_low_luc):
 * the scheme is CORSIA(-PLUS) AND the ISCC add-on is "Low-LUC" AND the selected scope
 * includes one of the two low-LUC criteria scopes.
 */
// The real scope-master entry is "202-07: Low ILUC-risk feedstock" (see AuditQ's
// certificate_production_draft.blade.php:889), so the optional I is required: matching
// only "low luc" never fired on production data and every CORSIA certificate silently
// got the ordinary four-column layout instead of the low-LUC one.
const LOW_LUC_SCOPE = /low[\s_-]*i?luc/i;

export const isLowLucVariant = (scheme, { addOn = '', scopes = [] } = {}) => {
  if (scheme !== ISCC_SCHEMES.CORSIA && scheme !== ISCC_SCHEMES.CORSIA_PLUS) return false;
  if (String(addOn || '').trim().toUpperCase() !== 'LOW-LUC') return false;
  return (Array.isArray(scopes) ? scopes : [scopes])
    .some((scope) => LOW_LUC_SCOPE.test(String(
      typeof scope === 'string' ? scope : scope?.name || scope?.code || scope?.label || '',
    )));
};

// Column types drive the input control: `material` and `scope` are master-data
// selects (GET /md/iscc-material, GET /md/iscc-scope/all); `text` is free text.
const material = (field, label) => ({ field, label, type: 'material' });
const scope = (field, label) => ({ field, label, type: 'scope' });
const text = (field, label) => ({ field, label, type: 'text' });

// ISCC EU -- the legacy default branch, shared by PLUS and CFC. Note the interleaved
// Material/Scope pairs and the two trailing columns the CRM form was missing.
const EU_COLUMNS = [
  material('inputMaterial', 'Input Material'),
  scope('inputScope', 'Input Scope'),
  material('outputMaterial', 'Output Material'),
  scope('outputScope', 'Output Scope'),
  text('ghgOption', 'GHG option'),
  text('euWaste', 'ISCC EU waste process applied'),
  text('saiFsa', 'SAI FSA'),
];

const JAPAN_FIT_COLUMNS = [
  material('inputMaterial', 'Input Material'),
  material('outputMaterial', 'Output Material'),
  text('ghgOption', 'GHG / LCA option'),
  text('certificationApproach', 'Certification approach of raw material'),
  text('addOns', 'Add-ons (voluntary)'),
];

// CORSIA reuses the euWaste field but labels it "ISCC waste process applied"
// (see the legacy comment at certificate_production_draft.blade.php:530).
const CORSIA_COLUMNS = [
  material('inputMaterial', 'Input Material'),
  material('outputMaterial', 'Output Material'),
  text('ghgOption', 'GHG / LCA option'),
  text('euWaste', 'ISCC waste process applied'),
];

// Low-LUC reuses inputMaterial as the "Raw Material" select -- legacy renders the same
// select box under a different header rather than introducing a new field.
const LOW_LUC_COLUMNS = [
  material('inputMaterial', 'Raw Material'),
  text('lowLucRiskApproach', 'Low LUC risk approach'),
  text('briefLowLucRiskPractices', 'Brief description of applied low LUC risk practices'),
];

export const ANNEX_1_COLUMNS = [
  material('inputMaterial', 'Input Material'),
  material('outputMaterial', 'Output Material'),
  text('addOns', 'Add-ons (voluntary)'),
  text('rawCategory', 'Raw material category'),
  text('saiFsa', 'SAI FSA'),
  text('fefac', 'FEFAC'),
];

/**
 * The Material table columns for a certificate. `context` carries the low-LUC inputs
 * ({ addOn, scopes }); omit it and the non-low-LUC layout is used.
 */
export const resolveMaterialColumns = (value, context = {}) => {
  const scheme = resolveIsccScheme(value);
  if (!scheme) return [];
  if (isLowLucVariant(scheme, context)) return LOW_LUC_COLUMNS;
  switch (scheme) {
    case ISCC_SCHEMES.JAPAN_FIT: return JAPAN_FIT_COLUMNS;
    case ISCC_SCHEMES.CORSIA:
    case ISCC_SCHEMES.CORSIA_PLUS: return CORSIA_COLUMNS;
    default: return EU_COLUMNS;
  }
};

/**
 * Annex I is ISCC PLUS only AND only when the operator ticks it -- legacy gate is
 * `@if($current_process->is_iscc_plus && $current_process->iscc_annex_1)`. It is a
 * stored decision, never derived from the scheme: a PLUS certificate may or may not
 * carry Annex I.
 */
export const supportsAnnex1 = (value) => resolveIsccScheme(value) === ISCC_SCHEMES.PLUS;

export const isAnnex1Active = (value, annex1Enabled) => supportsAnnex1(value) && annex1Enabled === true;

/** An empty row shaped for whichever columns apply, so no stale keys are persisted. */
export const buildEmptyMaterialRow = (columns = []) => columns.reduce(
  (row, column) => ({ ...row, [column.field]: '' }),
  {},
);
